import React from "react";
import { cockpit } from "@/api";
import type { NeoMailboxAvailability, NeoMailboxOffer } from "@/api";
import { Banner, FormField, Input } from "@/ds";
import { useT } from "../i18n";
import { isThrottled } from "./invitation.logic";

/**
 * Choosing a platform-hosted address (#710).
 *
 * The whole step is the local part, so it has to answer three questions while the person is
 * still typing: is this name allowed, is it free, and what am I actually agreeing to.
 *
 * **The rules are not in this file.** Availability and validity both come from the server on
 * every keystroke, because a copy of the reserved-name list in the browser would drift, and the
 * day it drifts the form either promises an address it cannot deliver or refuses one it could.
 * What lives here is the German wording for each machine-readable reason the server returns.
 *
 * The disclosure is shown at the point of choosing rather than after: the tenant is getting an
 * address they cannot log into with a mail client and cannot take with them. That is a fair
 * trade for not having to find IMAP settings, but only if they know before they choose.
 */

/** Debounce: long enough that ordinary typing makes one request, short enough to feel live. */
const AVAILABILITY_DEBOUNCE_MS = 350;

export type PickerState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "verdict"; verdict: NeoMailboxAvailability }
  | { kind: "unreachable" }
  /** The server asked for a pause (#1163). Not a verdict, and not a failure of the check. */
  | { kind: "throttled" };

/** Does this installation host mailboxes at all? Asked once; `null` while unknown. */
export function useNeoMailboxOffer(): NeoMailboxOffer | null {
  const [offer, setOffer] = React.useState<NeoMailboxOffer | null>(null);
  React.useEffect(() => {
    let live = true;
    cockpit.neoMailboxOffer().then(
      (result) => live && setOffer(result),
      // An installation that cannot answer is treated as one that does not offer it. Showing
      // the option and failing at the end would be worse than not showing it.
      () => live && setOffer({ offered: false, domain: null }),
    );
    return () => {
      live = false;
    };
  }, []);
  return offer;
}

/** The address this picker would provision, or null while the name is not usable. */
export function pickedAddress(state: PickerState): string | null {
  return state.kind === "verdict" && state.verdict.status === "available"
    ? state.verdict.emailAddress
    : null;
}

export function NeoMailboxPicker({
  localPart,
  onLocalPartChange,
  onStateChange,
  domain,
  inputId = "neo-mailbox-local-part",
  check,
}: {
  localPart: string;
  onLocalPartChange: (value: string) => void;
  /** Lifted so the surrounding form can disable its submit until the name is usable. */
  onStateChange?: (state: PickerState) => void;
  domain: string | null;
  inputId?: string;
  /**
   * Who to ask. Defaults to the owner-plane route; the invitation screen (#780) passes the
   * token-gated one, because the person choosing their address has no session yet. The wording
   * and the rules stay identical either way — only the authority behind the question differs.
   */
  check?: (localPart: string) => Promise<NeoMailboxAvailability>;
}) {
  const t = useT();
  const [state, setState] = React.useState<PickerState>({ kind: "idle" });

  const publish = React.useCallback(
    (next: PickerState) => {
      setState(next);
      onStateChange?.(next);
    },
    [onStateChange],
  );

  React.useEffect(() => {
    const trimmed = localPart.trim();
    if (!trimmed) {
      publish({ kind: "idle" });
      return;
    }
    publish({ kind: "checking" });
    let live = true;
    // Called through `cockpit` rather than lifted off it: an unbound method would work only for
    // as long as the client never uses `this`.
    const ask = check ?? ((value: string) => cockpit.neoMailboxAvailability(value));
    const timer = setTimeout(() => {
      ask(trimmed).then(
        (verdict) => live && publish({ kind: "verdict", verdict }),
        // A failed check is never reported as "free": the person would fill in the rest of the
        // form believing they have the name.
        (e: unknown) => live && publish(isThrottled(e) ? { kind: "throttled" } : { kind: "unreachable" }),
      );
    }, AVAILABILITY_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [localPart, publish, check]);

  const feedback = () => {
    if (state.kind === "idle") return null;
    if (state.kind === "checking") {
      return <span className="subtle">{t("neoMailbox.checking")}</span>;
    }
    if (state.kind === "unreachable") {
      return <span className="subtle">{t("neoMailbox.checkFailed")}</span>;
    }
    if (state.kind === "throttled") {
      return <span className="subtle" data-testid="neo-mailbox-throttled">{t("neoMailbox.throttled")}</span>;
    }
    const { verdict } = state;
    if (verdict.status === "available") {
      return (
        <span data-testid="neo-mailbox-available">
          {t("neoMailbox.available", { address: verdict.emailAddress })}
        </span>
      );
    }
    if (verdict.status === "taken") {
      return (
        <span data-testid="neo-mailbox-taken">
          {t("neoMailbox.taken", { address: verdict.emailAddress })}
        </span>
      );
    }
    if (verdict.status === "invalid") {
      // The server names the reason; the German wording is ours. An unknown reason code falls
      // back to the server's own message rather than to silence.
      const key = `neoMailbox.reason.${verdict.reason}`;
      return (
        <span data-testid="neo-mailbox-invalid">{t(key, undefined, verdict.message)}</span>
      );
    }
    return null;
  };

  return (
    <div className="stack-sm" data-testid="neo-mailbox-picker">
      <FormField label={t("neoMailbox.localPart.label")} htmlFor={inputId}>
        <Input
          id={inputId}
          value={localPart}
          autoComplete="off"
          spellCheck={false}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onLocalPartChange(e.target.value)}
          placeholder="kontakt"
        />
      </FormField>
      <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
        {domain ? `…@${domain}` : null}
      </p>
      <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>{feedback()}</p>
      <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
        {t("neoMailbox.localPart.rule")}
      </p>

      <Banner tone="info" title={t("neoMailbox.terms.title")}>
        <p style={{ margin: 0 }}>{t("neoMailbox.terms.body")}</p>
        <p style={{ margin: "var(--space-2) 0 0" }}>{t("neoMailbox.terms.leaving")}</p>
      </Banner>
    </div>
  );
}
